/**
 * The English string catalog — the ONLY populated locale (T9 i18n scaffold).
 *
 * Rules:
 *  - Every NEW user-facing string belongs here, rendered via t('key').
 *    The hardcodedStrings ratchet (src/__tests__/hardcodedStrings.test.ts)
 *    freezes the legacy inline strings per file and fails the suite when a
 *    file gains one — extraction shrinks the baseline, never grows it.
 *  - Do NOT add a second locale in a feature PR (half-translated is worse than monolingual).
 *  - Language is NEVER inferred from country/IP — resolution order is
 *    explicit user preference → Accept-Language → 'en' (see ./index.ts).
 */
import { enAds } from './enAds';
import { enBroadcasts } from './enBroadcasts';
import { enBoards } from './enBoards';
import { enCampaigns, enTemplates } from './enCampaigns';
import { enInbox } from './enInbox';
import { enFunnels } from './enFunnels';
import { enAgent } from './enAgent';
import { enAssistant } from './enAssistant';
import { enAssistantAgents } from './enAssistantAgents';
import { enConnect } from './enConnect';
import { enResponder } from './enResponder';
import { enTiktok } from './en.tiktok';
import { enIntegrations } from './enIntegrations';
import { enCanvas } from './enCanvas';
import { enMembers } from './enMembers';
import { enScheduling, enComposer } from './enScheduling';
import { enTour } from './enTour';
import { enPopups } from './en.popups';
import { enMyWork } from './enMyWork';
import { enAdmin } from './enAdmin';
import { enCustomers } from './enCustomers';
import { enInventory, enOrders } from './enInventory';
import { enLanding } from './enLanding';
import { enBilling } from './enBilling';
import { enShopifyBilling } from './enShopifyBilling';
import { enOnboarding } from './enOnboarding';
import { enChrome } from './enChrome';
import { enPaymentTiles } from './enPaymentTiles';

const enBase = {
  // ── Product brand ─────────────────────────────────────────────────────
  'brand.name': 'BuzzBit X',
  'brand.logoAlt': 'BuzzBit X',
  // ── Getting Started guide (T10) ───────────────────────────────────────
  'guide.title': 'Getting Started with BuzzBit X',
  'guide.intro':
    'Five steps from a fresh account to your first automated sale. Each step ends with a checkpoint so you can tell it worked.',
  'guide.backToApp': 'Back to the app',
  'guide.backToHome': 'Back to Home',
  'guide.checkpoint': 'Checkpoint',
  'guide.step1.title': '1. Set up your business',
  'guide.step1.body':
    'Pick your business type when you sign up: e-commerce, service, course, SaaS, coaching or content creator. Every type starts from its Business Hub; online stores add their products there. Connecting a Shopify store is coming soon.',
  'guide.step1.check': 'Your Business Hub shows the tabs for your business type. Online stores also see their products under Products and their customers under Customers.',
  'guide.step2.title': '2. Activate your first flow',
  'guide.step2.body':
    'Open Automations and pick a starter flow. Review the messages, then switch it on.',
  'guide.step2.check': 'The flow shows a green "Active" badge at the top of its card.',
  'guide.step3.title': '3. Connect a messaging channel',
  'guide.step3.body':
    'Under Account → Integrations, connect the messaging channels available for your account.',
  'guide.step3.check':
    'The channel card shows "Connected" with your page name. Send yourself a test message — it should appear in the Inbox.',
  'guide.step4.title': '4. Find your Inbox',
  'guide.step4.body':
    'Customer conversations from your connected channels and email land in the Inbox in the sidebar. Reply from one place; BuzzBit X delivers on the right channel and shows you delivery state per message.',
  'guide.step4.check': 'Your test message from step 3 is visible in the Inbox thread.',
  'guide.step5.title': '5. Watch the results',
  'guide.step5.body':
    'Analytics shows revenue, flow performance, and channel activity.',
  'guide.step5.check': 'The Analytics dashboard shows non-zero events for your business.',

  // ── Home-screen widgets card (Settings, native app only) ──────────────
  'widgets.title': 'Home-screen widgets',
  'widgets.subtitle':
    'Revenue, meetings, inbox, and automations — at a glance on your home screen.',
  'widgets.description':
    'Enabling widgets stores a read-only key on this device so the widget can refresh in the background. On iPhone, the same widget also appears on your Mac desktop (macOS Sonoma and later). You can disable and revoke it anytime.',
  'widgets.enable': 'Enable widgets on this device',
  'widgets.enabledNote': 'Widgets are enabled on this device. Add the',
  'widgets.brandName': 'BuzzBit X',
  'widgets.enabledNoteRest': 'widget from your home screen',
  'widgets.enabledNoteLockScreen': ' or Lock Screen',
  'widgets.disable': 'Disable and revoke key',

  // ── Integration re-auth banner ────────────────────────────────────────
  'reauth.single': 'Your {name} connection expired and needs to be reconnected.',
  'reauth.multiple': '{count} integrations ({names}) expired and need to be reconnected.',
  'reauth.reconnect': 'Reconnect',

  // ── Pipelines · social funnel (B2) ────────────────────────────────────
  'pipelines.social.revenueLabel': 'Revenue · social-attributed',
  'pipelines.social.journeyTitle': 'DM → purchase journey',
  'pipelines.social.empty':
    'No social DM activity in this period yet — Instagram & Messenger conversations, captured contacts, and their purchases will appear here.',

  // ── Brand DNA colors ──────────────────────────────────────────────────
  'brand.secondaryColor': 'Secondary Color',

  // ── Social calendar (kind-alignment §9) ───────────────────────────────
  'social.calendar.prev': 'Previous',
  'social.calendar.next': 'Next',
  'social.calendar.jumpToToday': 'Jump to today',
  'social.calendar.newPost': 'New Post',
  'social.calendar.holiday': 'Holiday',
  'social.calendar.blockedLegend': 'Blocked — click a day to toggle',
  'social.calendar.noPosting': 'No posting',
  'social.calendar.noPostingDay': 'No posting on this day',
  'social.calendar.emptyDay': 'Nothing scheduled on this day',
  'social.calendar.schedulePost': 'Schedule a post',
  'social.calendar.blocked': 'Blocked',
  'social.calendar.blockDay': 'Block day',
  'social.calendar.more': '+{count} more',

  // ── The two doors onto the same screen ────────────────────────────────
  // /calendar is the CORE nav entry (the whole business on one grid);
  // /marketing/social is the social toolkit. Same component, different title.
  'calendar.title': 'Calendar',
  'social.title': 'Social Studio',

  // ── Day drawer (the calendar cell is a summary; this is the detail) ────
  'calendar.day.empty': 'Nothing scheduled on this day.',
  'calendar.day.allDay': 'All day',
  'calendar.day.count': '{count} scheduled',
  'calendar.day.draftCount': '{count} drafts',
  'calendar.day.countWithDrafts': '{count} scheduled · {drafts} drafts',
  'calendar.day.agenda': 'Hour-by-hour agenda',
  'calendar.day.blockDay': 'Block day',
  // Posted vs Scheduled is decided by whether the slot has passed, so a
  // merchant reading today can tell what already went out from what is still
  // to come — the single question the agenda exists to answer.
  'calendar.day.posted': 'Posted',
  'calendar.day.scheduled': 'Scheduled',
  'calendar.day.meetings': 'Meetings',
  // The native video product. Named rather than called "meeting", because
  // every `type: 'meeting'` event is a CallRoom — see the drawer's header.
  'calendar.day.buzzCall': 'BuzzCall',
  'calendar.day.noMeetings': 'No meetings',
  'calendar.day.startBuzzCall': 'Start a BuzzCall meeting',
  'calendar.day.socialMedia': 'Social media',
  'calendar.day.noSocial': 'Nothing going out',
  'calendar.day.addPost': 'Schedule a post',
  'calendar.day.paidAds': 'Paid ads',
  'calendar.day.noAds': 'No campaigns running',
  // Quick-add. It collects the three things a post cannot be scheduled
  // without, then hands off to the Composer with the day already filled in —
  // a drawer that half-creates a post is worse than one that opens the tool
  // which finishes the job.
  'calendar.quickAdd.channel': 'Channel',
  'calendar.quickAdd.type': 'Type',
  'calendar.quickAdd.time': 'Time, e.g. 14:00',
  'calendar.quickAdd.add': 'Add',
  'calendar.quickAdd.cancel': 'Cancel',

  // ── Ads tab noun labels (kind-alignment §8) ───────────────────────────
  'ads.subtitle': 'Meta and Google Ads performance tied to your {noun}s.',
  'ads.colAiAction': 'AI Action',
  'ads.conversionsDefault': 'Conversions',
  'ads.conversionsNoun': '{noun}s',
  'ads.revenueDefault': 'Total Revenue',
  'ads.revenueNoun': '{noun} revenue',

  // ── Boards: monday-style groups + provenance (kind-alignment §7) ──────
  'boards.addItem': 'Add item',
  'boards.ungrouped': 'Items',
  'boards.statusUnset': '—',
  'boards.deleteItem': 'Delete row',
  'boards.deleteItemConfirm': 'Delete this row? This cannot be undone.',
  'boards.newGroup': 'New group',
  'boards.newGroupName': 'New group',
  'boards.renameGroup': 'Rename group',

  // ── Unattributed revenue (why a lifetime spend reads zero) ────────────
  'customers.unattributed.title': 'Some lifetime totals are incomplete',
  'customers.unattributed.body':
    '{amount} across {orders} orders is not linked to any customer, so it does not appear in anyone’s lifetime spend.',
  'customers.unattributed.cause':
    'This usually means the store did not share a customer email with the order. A ₪0 here may mean “not linked”, not “never bought”.',

  // ── Services catalog (kind-alignment §5) ──────────────────────────────
  'services.count': '{count} in your {catalog}',
  'services.new': 'New service',
  'services.bookingLink': 'Booking link',
  'services.edit': 'Edit',
  'services.duration': '{minutes} min',
  'services.noDuration': 'Duration not set',
  'services.loadError': 'Could not load your services.',
  'services.saveError': 'Could not save the service.',
  'services.deleteError': 'Could not delete the service.',
  'services.empty.title': 'Add what you actually sell',
  'services.empty.body':
    'Bookings, reminders and no-show recovery all key off your service menu.',
  'services.empty.cta': 'Add your first service',
  'services.editor.addTitle': 'Add a service',
  'services.editor.editTitle': 'Edit service',
  'services.editor.close': 'Close',
  'services.editor.hint':
    'What you sell, how long it takes and what it costs. Reminders, no-show recovery and your booking link all use this.',
  'services.editor.name': 'Service name',
  'services.editor.namePlaceholder': 'e.g. Deep tissue massage',
  'services.editor.duration': 'Duration (minutes)',
  'services.editor.price': 'Price',
  'services.editor.icon': 'Icon',
  'services.editor.save': 'Save service',
  'services.editor.add': 'Add service',
  'services.editor.cancel': 'Cancel',
  'services.editor.delete': 'Delete',

  // ── Lead pipeline (ServicesCatalogTab kanban, kit PipelineKanban) ─────
  'pipeline.title': 'Lead pipeline',
  'pipeline.hint': 'Drag a lead across stages — it writes straight to the customer record.',
  'pipeline.stage.new': 'New',
  'pipeline.stage.contacted': 'Contacted',
  'pipeline.stage.qualified': 'Qualified',
  'pipeline.stage.proposal': 'Proposal',
  'pipeline.stage.won': 'Won',
  'pipeline.stage.lost': 'Lost',
  'pipeline.empty': 'No leads yet — they arrive from your landing pages, DMs and ads.',
  'pipeline.dropHere': 'Drop here',
  'pipeline.loadError': 'Could not load your leads.',
  'pipeline.moveError': 'Could not move the lead — change was undone.',

  // ── Setup checklist (kind-alignment §4) ───────────────────────────────
  'setup.title': 'Get set up',
  'setup.subtitle': '{done} of {total} done · {kind}',
  'setup.pill': 'Setup {done}/{total}',
  'setup.collapse': 'Collapse setup checklist',
  // Task 98: steps are stored per business kind. While the kind is unknown a
  // tick would be filed under the wrong one, so nothing is recorded and the
  // merchant is told rather than left thinking it saved.
  'setup.kindUnknown': "We couldn't confirm your business kind, so steps aren't being saved yet.",
  'setup.kindUnknownRetry': 'Try again',
  'onboarding.kind.tracks': 'Tracks {noun}s',
  'setup.cta.integrations': 'Open Integrations',
  'setup.cta.hub': 'Open Business Hub',
  'setup.cta.funnels': 'Show my funnels',
  'setup.cta.inbox': 'Open Inbox',
  'setup.cta.automations': 'Open Automations',
  'setup.cta.calendar': 'Open Calendar',
  'setup.cta.brand': 'Open Brand DNA',
  'setup.step.store.title': 'Connect your store',
  'setup.step.store.body': 'Products, orders and customers sync in. Every funnel reads from this.',
  'setup.step.menu.title': 'Add what you sell',
  'setup.step.menu.body': 'Name, duration and price. Bookings, reminders and no-show recovery all key off your service menu.',
  'setup.step.wa.title': 'Connect WhatsApp',
  'setup.step.wa.body': 'Where your clients actually message you — speed-to-lead replies in under a minute.',
  'setup.step.email.title': 'Connect your sending domain',
  'setup.step.email.body': 'Your emails go out from your own domain, not ours.',
  'setup.step.pay.title': 'Connect payments',
  'setup.step.pay.body': 'Checkout and subscription funnels read your real payment events.',
  'setup.step.ads.title': 'Link your ad account',
  'setup.step.ads.body': 'Ad clicks get attributed all the way to the conversion, so ROAS is real.',
  'setup.step.socialDm.title': 'Connect Instagram & Messenger',
  'setup.step.socialDm.body': 'Comments and DMs land in one inbox and can answer themselves.',
  'setup.step.availability.title': 'Set your availability',
  'setup.step.availability.body': 'Bookings, reminders and no-show recovery key off your real working hours. An empty week means "no open times" on your booking page.',
  'setup.footer.reassure': 'Funnels switch on as you connect.',
  'setup.footer.changeKind': 'Change business',
  'setup.step.funnels.title': 'See what turned on',
  'setup.step.funnels.body': 'Your funnels switched themselves on as you connected — see what is already running.',
  'setup.step.inbox.title': 'Meet the inbox',
  'setup.step.leads.title': 'Check your lead board',
  'setup.step.leads.body': 'Every enquiry, its stage, and which automation is chasing it.',
  'setup.step.flows.title': 'Look inside a flow',
  'setup.step.flows.body': 'Every funnel is an editable flow — open one and change a delay or a message.',
  'setup.step.socialCal.title': 'Queue your content',
  'setup.step.brand.title': 'Confirm your Brand DNA',
  'setup.step.brand.body': 'Tone, colours and offers — everything BuzzBit X sends copies this.',

  'boards.automations.runAutomation': 'Run a canvas automation',
  'boards.automations.workflowId': 'Automation workflow ID',
  'social.replies.title': 'Comment → DM automation',
  'social.replies.intro': 'When someone comments a keyword on your posts, send them a DM automatically. Keywords match whole words only, and each person gets one DM per post.',
  'social.replies.new': 'New rule',
  'social.replies.newTitle': 'New comment rule',
  'social.replies.editTitle': 'Edit comment rule',
  'social.replies.name': 'Rule name',
  'social.replies.namePlaceholder': 'e.g. Price list on launch post',
  'social.replies.anyComment': 'Reply to every comment (no keyword needed)',
  'social.replies.keywords': 'Keywords',
  'social.replies.keywordsPlaceholder': 'price, link, info',
  'social.replies.keywordsHint': 'Comma-separated. Matches whole words — "like" will not match "dislike".',
  'social.replies.excludeKeywords': 'Exclude keywords (optional)',
  'social.replies.excludeKeywordsPlaceholder': 'giveaway, sold out',
  'social.replies.excludeKeywordsHint': 'Comments containing any of these never get a reply — even when "reply to every comment" is on. Whole-word matching.',
  'social.replies.dmText': 'DM message',
  'social.replies.dmPlaceholder': 'Hey {{username}}! Here is the link you asked for…',
  'social.replies.publicReply': 'Also reply publicly under the comment',
  'social.replies.publicReplyPlaceholder': 'Check your DMs! 📩',
  'social.replies.postScope': 'Limit to specific posts (optional)',
  'social.replies.postScopePlaceholder': 'Post IDs, comma-separated — empty = all posts',
  'social.replies.postScopeHint': 'Empty means every post and reel — including boosted posts (they keep the same post ID). Ads created only in Ads Manager use their own ID, so leave this empty to cover them.',
  'social.replies.empty': 'No comment rules yet',
  'social.replies.emptyHint': 'Pick a keyword (or every comment), write the DM, and every matching comment turns into a conversation.',
  'social.replies.emptyCta': 'Create your first rule',
  'social.replies.loadFailed': 'Couldn’t load your comment rules.',
  'social.replies.failedHint': 'Check your connection and try again.',
  'social.replies.retry': 'Try again',
  'social.replies.rowAnyComment': 'Any comment',
  'social.replies.rowKeywords': 'Keywords: {keywords}',
  'social.replies.rowScoped': '{count} posts',
  'social.replies.rowAllPosts': 'All posts',
  'social.replies.rowDms': '{count} DMs',
  'social.replies.on': 'On',
  'social.replies.off': 'Off',
  'social.replies.save': 'Save rule',
  'social.replies.saving': 'Saving…',
  'social.replies.saveError': "Couldn't save the rule.",
  'social.replies.cancel': 'Cancel',
  'social.replies.edit': 'Edit',
  'social.replies.delete': 'Delete',
  // ── Agent canvas: trigger keyword filter ──
  'agent.trigger.keywords': 'Keywords',
  'agent.trigger.keywordsPlaceholder': 'e.g. price, discount, order',
  'agent.trigger.keywordsHint': 'The flow fires only when the inbound message contains one of these.',

  // ── Integration row + its setup modal (shared by every integration) ──
  // Marketing preferences on a contact. WhatsApp consent was readable in the
  // UI and settable only by inbound STOP/START, the Shopify checkout extension
  // and an MCP tool, so a merchant on any other storefront could not record a
  // consent their customer had actually given.
  'contacts.whatsappConsentLabel': 'WhatsApp messages',
  'contacts.whatsappConsentDescription': 'Contact agreed to receive WhatsApp messages',
  'contacts.whatsappOptInSaved': 'WhatsApp consent recorded',
  'contacts.whatsappOptOutSaved': 'WhatsApp consent withdrawn',
  'contacts.whatsappConsentFailed': 'Could not update WhatsApp consent. Please try again.',
  'contacts.emptyTitle': 'No contacts yet',
  'integrations.connected': 'Connected',
  'integrations.notConnected': 'Not connected',
  'integrations.needsReauth': 'Needs re-authentication',
  'integrations.connect': 'Connect',
  'integrations.manage': 'Manage',
  'integrations.viewProfile': 'View Profile',

  // ── Integrations page chrome ──────────────────────────────────────────
  'integrations.title': 'Integrations',
  'integrations.subtitle': 'Connect Shopify, Meta, email, ads, and other tools.',
  'integrations.searchPlaceholder': 'Search integrations',
  'integrations.emptyTitle': 'No integrations found',
  'integrations.emptyBody': 'Try a different search or category.',
  'integrations.beta': 'Beta',

  // ── Registry integration detail sheet ─────────────────────────────────
  'integrations.back': 'Back',
  'integrations.details': 'Details',
  'integrations.detailType': 'Connector type',
  'integrations.detailCategory': 'Category',
  'integrations.detailAuth': 'Authentication',
  'integrations.detailAuthor': 'Author',
  'integrations.typeApp': 'App',
  'integrations.author': 'BuzzBit X',
  'integrations.authOauth': 'Sign in with the provider',
  'integrations.authApiKey': 'API key',
  'integrations.authCredentials': 'Account credentials',
  'integrations.authWebhook': 'Webhook',
  'integrations.authNone': 'No credentials needed',
  'integrations.invoicingProvider': 'Provider',
  'integrations.invoicingApiSecret': 'API secret',
  'integrations.cardcomTerminalNumber': 'Terminal number',
  'integrations.cardcomApiName': 'API name',

  // ── Unified Meta card (Integrations) ──────────────────────────────────
  'meta.cardTitle': 'Meta — Messenger & Instagram',
  'meta.cardSubtitle': 'One connection for Messenger + Instagram DMs — receive and reply from the inbox.',
  'meta.pagePrefix': 'Page:',
  'meta.noIgLinked': 'no Instagram account linked to this page',
  'meta.connected': 'Connected',
  'meta.notConnected': 'Not connected',
  'meta.connect': 'Connect Meta',
  'meta.test': 'Test',
  'meta.disconnect': 'Disconnect',
  'meta.whatsappTitle': 'WhatsApp Business',
  'meta.whatsappSubtitle': 'Connect the WhatsApp Business API for customer conversations.',
  'meta.whatsappConnect': 'Connect WhatsApp',


  // ── Inbox human-handoff controls ──────────────────────────────────────
  'inbox.aiPaused': 'Automation paused',
  'inbox.inControl': "You're in control of this conversation.",
  'inbox.resumeAi': 'Resume automation',
  'inbox.takeOver': 'Take over',

  // ── Flow-builder handoff node fields ──────────────────────────────────
  'handoffNode.messageLabel': 'Message to customer',
  'handoffNode.priorityLabel': 'Priority',
  'handoffNode.priorityNormal': 'Normal',
  'handoffNode.priorityHigh': 'High',
  'handoffNode.priorityUrgent': 'Urgent',
  'handoffNode.notifyTeam': 'Notify the team (in-app + push)',

  // ── Automations canvas: channel readiness + business-kind alignment ────
  'canvas.status.connect': 'Connect',
  'canvas.status.auth': 'Auth',
  'canvas.status.builtIn': 'Built-in',
  'canvas.status.yourClaude': 'Your Claude',
  'canvas.status.notConnected': 'This channel is not connected yet.',
  'canvas.status.connectLink': 'Connect it',
  'canvas.status.staysDraft': 'the step stays saved as a draft until you do.',
  'canvas.status.fixHint': '{reason} Connect it in {path}.',
  'canvas.active.empty.title': 'No automations yet',
  'canvas.active.empty.body': "Build one on the canvas, start from a template, or describe one in Chat — it'll appear here.",
  'canvas.active.empty.openBuilder': 'Open Builder',
  'canvas.active.empty.browseTemplates': 'Browse Templates',
  'canvas.ai.viaWorkspaceClaude':
    'Relayed to your connected Claude key — runs at step time only. No platform model.',
  'canvas.ai.viaCredential':
    'Calls {credential} directly — your endpoint, your key, injected server-side at step time.',
  'canvas.templates.forYourBusiness': 'Your business',
  'canvas.templates.builtFor': 'Built for a {kind}',
  'canvas.templates.kindFirst': 'Flows built for a {kind} come first.',
  // ── BuzzTalk guest invite (magic-link accept page) ────────────────────
  'chatInvite.heading': 'Join the conversation',
  'chatInvite.loading': 'Loading invitation…',
  'chatInvite.invalidLink': 'This invitation link is invalid.',
  'chatInvite.expired':
    'This invitation has expired. Ask whoever invited you to send a new one.',
  'chatInvite.used':
    'This invitation has already been used. Ask whoever invited you to send a new one.',
  'chatInvite.failed': 'Failed to accept the invitation.',
  'chatInvite.invitedTo': "You've been invited to {group} in {workspace}",
  'chatInvite.scope':
    'You can read and post in this group and see its tasks. Nothing else in {workspace} is shared with you.',
  'chatInvite.switchWarning':
    "You're currently signed in as {current}. Accepting switches this browser to {invited}.",
  'chatInvite.nameLabel': 'Your name',
  'chatInvite.namePlaceholder': 'e.g. Dana Levi',
  'chatInvite.passwordLabel': 'Choose a password',
  'chatInvite.passwordPlaceholder': 'At least 8 characters',
  'chatInvite.passwordTooShort': 'Password must be at least 8 characters.',
  'chatInvite.existingAccount':
    'This email already has a BuzzBit account. Accepting adds this group to it.',
  'chatInvite.signInRequired':
    'This invitation is for {email}, which already has a BuzzBit account. Sign in as {email} to accept it — an invitation link is not proof of who you are.',
  'chatInvite.signInCta': 'Sign in as {email}',
  'chatInvite.submitExisting': 'Accept invitation',
  'chatInvite.submitNew': 'Create account & join',
  'chatInvite.ignoreHint': 'Invited by mistake? You can simply ignore the email.',

  // ── BuzzTalk guest invite (inviter-side dialog) ───────────────────────
  'chatInvite.dialogTitle': 'Invite guest to {group}',
  'chatInvite.dialogScope':
    'Guests can read and post in this group only. They cannot see anything else in your workspace.',
  'chatInvite.emailLabel': 'Guest email',
  'chatInvite.emailPlaceholder': 'freelancer@example.com',
  'chatInvite.sent': 'Invitation emailed to {email}.',
  'chatInvite.sendFailed':
    "We couldn't send the email. Share this link with them directly instead.",
  'chatInvite.linkLabel': 'Magic link',
  'chatInvite.linkHint': 'Single-use, expires in 7 days. The guest joins when they open it.',
  'chatInvite.send': 'Send invite',
  'chatInvite.cancel': 'Cancel',
  'chatInvite.done': 'Done',

  // ── Workspace switcher (account menu) ─────────────────────────────────
  'workspaceSwitcher.heading': 'Workspaces',

  // ── Business hub cards ────────────────────────────────────────────────
  'acquisition.loadFailed': "Couldn't load acquisition data.",

  // ── API keys ──────────────────────────────────────────────────────────
  'apiKey.copyOnce':
    "Copy it now — it won't be shown again. You can revoke it any time in Developers.",
  'apiKey.expiresOn': 'This key expires on {date}. Generate a new one before then to stay connected.',

  // ── Classic Automations canvas (W11) ──────────────────────────────────
  'canvas.newAutomation': 'New Automation',
  'canvas.loading': 'Loading...',
  'canvas.noFlows': 'No automations yet',
  'canvas.runs': 'runs',
  'canvas.triggerType': 'Trigger Type',
  'canvas.triggerTypeAi': 'AI Intent',
  'canvas.triggerTypeKeyword': 'Keyword Match',
  'canvas.triggerTypeEvent': 'Event',
  'canvas.triggerTypeManual': 'Manual (test only)',
  'canvas.intent': 'Intent',
  'canvas.intentPlaceholder': 'e.g., TRACK_ORDER',
  'canvas.keywords': 'Keywords',
  'canvas.keywordsPlaceholder': 'price, shipping, refund',
  'canvas.event': 'Event',
  'canvas.selectEvent': 'Select an event…',
  'canvas.channel': 'Channel',
  'canvas.allChannels': 'All Channels',
  'canvas.channelWhatsapp': 'WhatsApp',
  'canvas.channelInstagram': 'Instagram',
  'canvas.channelMessenger': 'Messenger',
  'canvas.description': 'Description',
  'canvas.descriptionPlaceholder': 'What triggers this flow?',
  // ── BuzzTalk ──────────────────────────────────────────────────────────
  'buzztalk.loadFailed': "Couldn't load your conversations. Retry to try again.",
  'buzztalk.retry': 'Retry',
  'buzztalk.loadingConversations': 'Loading conversations',
  'buzztalk.loadingMessages': 'Loading messages',
  'buzztalk.loadingTasks': 'Loading tasks',
  'buzztalk.loadingPages': 'Loading pages',
  'buzztalk.noGroups': 'No groups yet — create one to start chatting.',
  'buzztalk.selectGroup': 'Select a group from the list.',
  'buzztalk.newGroup': 'New group',
  'buzztalk.noMessages': 'No messages yet. Be the first to say hello.',
  'buzztalk.noSearchResults': 'No matches.',
  'buzztalk.emptyGroups': 'No groups yet.',
  'buzztalk.createFirstGroup': 'Create your first group',
  'buzztalk.sendFailed': "Couldn't send your message. Try again.",
  'buzztalk.uploadFailed': "Couldn't upload that file. Try again.",
  'buzztalk.fileTooLarge': 'File is too large (max {max}MB).',
  'buzztalk.imageTypeUnsupported': 'Use JPEG, PNG, WebP, or GIF images.',
  'buzztalk.promoteFailed': "Couldn't promote that message to a task.",
  'buzztalk.promoteOk': 'Message promoted to a task.',
  'buzztalk.docsLoadFailed': "Couldn't load docs. Retry to try again.",
  'buzztalk.membersLoadFailed': "Couldn't load member lessons. Retry to try again.",
  'buzztalk.tabBuzzTalk': 'BuzzTalk',
  'buzztalk.tabDocs': 'Docs',
  'buzztalk.tabMembers': 'Members',
  'buzztalk.tabGetStarted': 'Get Started',
  'buzztalk.newCallLink': 'New call link',
  'buzztalk.newCallLinkShort': 'New',

  // ── YouTube upload settings (Social Automator composer) ───────────────
  'youtube.settingsTitle': 'YouTube Settings',
  'youtube.visibility': 'Visibility',
  'youtube.visibilityPrivate': 'Private — only you can watch it',
  'youtube.visibilityUnlisted': 'Unlisted — anyone with the link can watch',
  'youtube.visibilityPublic': 'Public — listed on your channel and searchable',
  'youtube.visibilityHint': 'You can change this on YouTube afterwards, but a video that goes public cannot be un-seen — BuzzBit uploads privately unless you choose otherwise.',
  'youtube.needsVideo': 'YouTube needs a video. Add one above, or unselect YouTube.',
} as const;
// Domain slices live in sibling modules so this file stays under the 500-line ceiling (a merge
// hotspot several branches append to). Composed here, so `MessageKey` still covers every key.
export const en = { ...enBase, ...enAds, ...enBroadcasts, ...enBoards, ...enCampaigns, ...enTemplates, ...enInbox, ...enFunnels, ...enAgent, ...enAssistant, ...enAssistantAgents, ...enConnect, ...enResponder, ...enTiktok, ...enCanvas, ...enIntegrations, ...enPopups, ...enMyWork, ...enAdmin, ...enMembers, ...enScheduling, ...enComposer, ...enTour, ...enCustomers, ...enInventory, ...enOrders, ...enLanding, ...enBilling, ...enShopifyBilling, ...enOnboarding, ...enChrome, ...enPaymentTiles } as const;
